import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { FrameState, ReferenceFrame, identityFrameState } from './ReferenceFrame';

export interface TransformedState {
  position: Vec3;
  velocity: Vec3;
}

/**
 * Resolves positions and velocities between arbitrary reference frames.
 *
 * Everything is composed through the root frame. That keeps the implementation
 * small and, for the depth of hierarchy this game uses (root -> planet -> fixed
 * -> local, i.e. four levels), costs nothing measurable.
 *
 * Velocity composition includes the `omega x r` term, so a body sitting still
 * on a rotating planet correctly reports orbital-frame velocity equal to the
 * surface rotation speed.
 */
export class ReferenceFrameManager {
  private frames = new Map<string, ReferenceFrame>();
  private rootId: string;
  /** Cache of root-relative frame states, invalidated whenever time advances. */
  private cache = new Map<string, FrameState>();
  private cacheTime = Number.NaN;

  constructor(root: ReferenceFrame) {
    this.rootId = root.id;
    this.frames.set(root.id, root);
  }

  get root(): string {
    return this.rootId;
  }

  register(frame: ReferenceFrame): void {
    if (this.frames.has(frame.id)) {
      throw new Error(`Reference frame "${frame.id}" is already registered`);
    }
    if (frame.parentId !== null && !this.frames.has(frame.parentId)) {
      throw new Error(`Reference frame "${frame.id}" declares unknown parent "${frame.parentId}"`);
    }
    this.frames.set(frame.id, frame);
    this.cacheTime = Number.NaN;
  }

  has(id: string): boolean {
    return this.frames.has(id);
  }

  get(id: string): ReferenceFrame {
    const f = this.frames.get(id);
    if (!f) throw new Error(`Unknown reference frame "${id}"`);
    return f;
  }

  list(): ReferenceFrame[] {
    return Array.from(this.frames.values());
  }

  /** Chain of frame ids from `id` up to and including the root. */
  chainToRoot(id: string): string[] {
    const chain: string[] = [];
    let cur: string | null = id;
    let guard = 0;
    while (cur !== null) {
      chain.push(cur);
      const frame: ReferenceFrame = this.get(cur);
      cur = frame.parentId;
      if (++guard > 64) throw new Error(`Reference frame cycle detected at "${id}"`);
    }
    return chain;
  }

  /** Pose of `id` expressed in the root frame at time `t`. */
  stateInRoot(id: string, t: number): FrameState {
    if (this.cacheTime !== t) {
      this.cache.clear();
      this.cacheTime = t;
    }
    const cached = this.cache.get(id);
    if (cached) return cached;

    const frame = this.get(id);
    let result: FrameState;
    if (frame.parentId === null) {
      result = identityFrameState();
    } else {
      const parent = this.stateInRoot(frame.parentId, t);
      const local = frame.stateAt(t);

      // Position: p_root = p_parent + R_parent * p_local
      const rotated = parent.orientation.rotate(local.position);
      const position = Vec3.addVectors(parent.position, rotated);

      // Velocity: v_root = v_parent + R_parent * v_local + omega_parent x (R_parent * p_local)
      const carried = Vec3.crossVectors(parent.angularVelocity, rotated);
      const velocity = Vec3.addVectors(parent.velocity, parent.orientation.rotate(local.velocity)).add(carried);

      const orientation = parent.orientation.clone().multiply(local.orientation);
      const angularVelocity = Vec3.addVectors(parent.angularVelocity, parent.orientation.rotate(local.angularVelocity));

      result = { position, velocity, orientation, angularVelocity };
    }

    this.cache.set(id, result);
    return result;
  }

  /** Converts a position from frame `fromId` into frame `toId`. */
  transformPosition(position: Vec3, fromId: string, toId: string, t: number): Vec3 {
    if (fromId === toId) return position.clone();
    const from = this.stateInRoot(fromId, t);
    const to = this.stateInRoot(toId, t);
    const rootPos = Vec3.addVectors(from.position, from.orientation.rotate(position));
    const rel = Vec3.subVectors(rootPos, to.position);
    return to.orientation.rotateInverse(rel);
  }

  /** Converts a direction (no translation) between frames. */
  transformDirection(direction: Vec3, fromId: string, toId: string, t: number): Vec3 {
    if (fromId === toId) return direction.clone();
    const from = this.stateInRoot(fromId, t);
    const to = this.stateInRoot(toId, t);
    return to.orientation.rotateInverse(from.orientation.rotate(direction));
  }

  /**
   * Converts a full kinematic state between frames, including the rotating-frame
   * velocity term. This is the function to use when a vessel crosses a sphere of
   * influence boundary or lifts off from a rotating surface: it guarantees the
   * trajectory stays continuous through the frame change.
   */
  transformState(position: Vec3, velocity: Vec3, fromId: string, toId: string, t: number): TransformedState {
    if (fromId === toId) return { position: position.clone(), velocity: velocity.clone() };
    const from = this.stateInRoot(fromId, t);
    const to = this.stateInRoot(toId, t);

    const rotatedPos = from.orientation.rotate(position);
    const rootPos = Vec3.addVectors(from.position, rotatedPos);
    const rootVel = Vec3.addVectors(from.velocity, from.orientation.rotate(velocity)).add(
      Vec3.crossVectors(from.angularVelocity, rotatedPos),
    );

    const relPos = Vec3.subVectors(rootPos, to.position);
    const relVel = Vec3.subVectors(rootVel, to.velocity).sub(Vec3.crossVectors(to.angularVelocity, relPos));

    return {
      position: to.orientation.rotateInverse(relPos),
      velocity: to.orientation.rotateInverse(relVel),
    };
  }

  /** Converts an orientation between frames. */
  transformOrientation(orientation: Quat, fromId: string, toId: string, t: number): Quat {
    if (fromId === toId) return orientation.clone();
    const from = this.stateInRoot(fromId, t);
    const to = this.stateInRoot(toId, t);
    return to.orientation.inverse().multiply(from.orientation).multiply(orientation).normalize();
  }

  /**
   * Converts an angular velocity between frames. Note this subtracts the target
   * frame's own rotation, so a vessel locked to a rotating surface reads zero
   * angular velocity in the body-fixed frame.
   */
  transformAngularVelocity(omega: Vec3, fromId: string, toId: string, t: number): Vec3 {
    if (fromId === toId) return omega.clone();
    const from = this.stateInRoot(fromId, t);
    const to = this.stateInRoot(toId, t);
    const rootOmega = Vec3.addVectors(from.angularVelocity, from.orientation.rotate(omega));
    return to.orientation.rotateInverse(Vec3.subVectors(rootOmega, to.angularVelocity));
  }

  /** Drops any cached frame states. Call after retiming or reseeding the universe. */
  invalidate(): void {
    this.cache.clear();
    this.cacheTime = Number.NaN;
  }
}
